/**
 * Which Diagnosegruppe a prescription form means.
 *
 * A code is not unique: "PS4" exists once for the Kasse Rezept-typ (id "PS4",
 * Heilmittel MFB/SPB/HLT) and once for the Blankoverordnung (id "PS4_blanko",
 * HLT/PFB under § 125a Ergotherapie). The Blanko form keeps the CODE in its
 * state and used to turn it back into an id with the first catalog row that
 * matched — so an Ergo Blanko Rx was saved against the Kasse group and then
 * validated with the Kasse Heilmittel ("PFB nicht zulässig"). Every lookup here
 * is scoped to the groups that actually apply to the Rx first.
 */

export interface DiagnosisGroupOption {
  id: string;
  code: string;
  therapyType?: string | null;
  RxType?: { id?: string | null } | null;
}

/** The groups a Rezept-typ offers for a discipline; untyped groups apply to all. */
export function diagnosisGroupsForRx<T extends DiagnosisGroupOption>(
  groups: T[] | undefined | null,
  rxTypeId: string | undefined | null,
  therapyType: string | undefined | null,
): T[] {
  if (!rxTypeId) return [];
  return (groups ?? []).filter(
    (group) =>
      group.RxType?.id === rxTypeId && (!group.therapyType || group.therapyType === therapyType),
  );
}

/**
 * The group a form value names — by id or by code, preferring the groups that
 * apply to this Rx. Outside them, only an id or an unambiguous code is trusted:
 * guessing between two groups with the same code is exactly the defect above.
 */
export function findDiagnosisGroup<T extends DiagnosisGroupOption>(
  value: string | undefined | null,
  applicable: T[],
  allGroups: T[] | undefined | null,
): T | undefined {
  if (!value) return undefined;
  const scoped = applicable.find((g) => g.id === value) ?? applicable.find((g) => g.code === value);
  if (scoped) return scoped;

  // The Kasse groups use their code as their id ("PS4"), so an id match alone is
  // not proof: "PS4" is also the code of PS4_blanko. Only a value that names
  // exactly one row, by id or by code, is resolved.
  const all = allGroups ?? [];
  const matches = all.filter((g) => g.id === value || g.code === value);
  return matches.length === 1 ? matches[0] : undefined;
}

/**
 * The group to pre-select, if the choice is not really a choice.
 *
 * A Physio Blankoverordnung has exactly one Diagnosegruppe (EX), so filling it
 * in saves a click. An Ergo one has three (SB1, PS3, PS4) and must be chosen:
 * the form used to put "EX" there regardless, a group that does not exist for
 * Ergotherapie.
 */
export function defaultDiagnosisGroup<T extends DiagnosisGroupOption>(applicable: T[]): T | undefined {
  return applicable.length === 1 ? applicable[0] : undefined;
}
